/**
 * Bilingual Route Mapping
 *
 * Maps English routes to their Spanish equivalents and vice versa.
 */

export const routeMap = {
  en: {
    home: "/",
    about: "/about",
    contact: "/contact",
    privacyPolicy: "/privacy-policy",
    termsOfService: "/terms-of-service",
    services: "/services",
    websiteDevelopment: "/services/website-development",
    localSeo: "/services/local-seo",
    technicalSeo: "/services/technical-seo",
    googleBusinessProfile: "/services/google-business-profile",
    socialMediaManagement: "/services/social-media-management",
    digitalMarketing: "/services/digital-marketing",
  },
  es: {
    home: "/es",
    about: "/es/sobre-nosotros",
    contact: "/es/contacto",
    privacyPolicy: "/es/politica-de-privacidad",
    termsOfService: "/es/terminos-de-servicio",
    services: "/es/servicios",
    websiteDevelopment: "/es/servicios/desarrollo-web",
    localSeo: "/es/servicios/seo-local",
    technicalSeo: "/es/servicios/seo-tecnico",
    googleBusinessProfile: "/es/servicios/google-business-profile",
    socialMediaManagement: "/es/servicios/gestion-redes-sociales",
    digitalMarketing: "/es/servicios/marketing-digital",
  },
} as const;

export type Language = "en" | "es";
export type RouteKey = keyof typeof routeMap.en;

/**
 * Get the equivalent route in another language
 */
export const getAlternateRoute = (
  currentPath: string,
  targetLang: Language
): string => {
  const isSpanish = currentPath.startsWith("/es");
  const currentLang: Language = isSpanish ? "es" : "en";

  // Already in target language
  if (currentLang === targetLang) {
    return currentPath;
  }

  // Find the route key for current path
  const currentRoutes = routeMap[currentLang];
  const targetRoutes = routeMap[targetLang];

  for (const [key, path] of Object.entries(currentRoutes)) {
    if (path === currentPath) {
      return targetRoutes[key as RouteKey];
    }
  }

  // Fallback: simple prefix manipulation
  if (targetLang === "es") {
    return `/es${currentPath === "/" ? "" : currentPath}`;
  }

  // Remove /es prefix for English
  const withoutPrefix = currentPath.replace(/^\/es/, "");
  return withoutPrefix || "/";
};

/**
 * Get route by key and language
 */
export const getRoute = (key: RouteKey, lang: Language): string => {
  return routeMap[lang][key];
};
